import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { catchError, tap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { SnackbarV3Service } from '@gea/digital-ui-lib';
import { ApiUrlService } from '../services/api-url.service';
import { PermissionService } from '../permissions/permission.service';

const PERMISSION_TOKEN_HEADER = 'x-permission-token';

export const brsInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const snackbarService = inject(SnackbarV3Service);
  const permissionService = inject(PermissionService);
  const apiBaseUrl = inject(ApiUrlService).baseUrl;

  if (!req.url.startsWith(apiBaseUrl)) {
    return next(req);
  }

  const storedToken = permissionService.token();
  const outgoingReq = storedToken ? req.clone({ setHeaders: { [PERMISSION_TOKEN_HEADER]: storedToken } }) : req;

  return next(outgoingReq).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const newToken = event.headers.get(PERMISSION_TOKEN_HEADER);
        if (newToken && newToken !== storedToken) {
          permissionService.setToken(newToken);
        }
      }
    }),
    catchError((err: HttpErrorResponse) => {
      if (!isSuppressedError(req, err)) {
        snackbarService.add({
          summary: 'Error',
          detail: getErrorDetails(err),
          severity: 'error',
        });
        console.error('Error occurred:', err);
      }
      return throwError(() => err);
    })
  );
};

const suppressedErrors: { urlPattern: RegExp; status: number }[] = [
  { urlPattern: /\/projects\/[^/]+\/reportset$/, status: 404 },
  { urlPattern: /\/projects\/[^/]+$/, status: 409 },
  { urlPattern: /\/projects\/[^/]+\/products\/[^/]+$/, status: 409 },
  { urlPattern: /\/projects\/[^/]+\/products\/[^/]+\/group(-name)?$/, status: 409 },
  { urlPattern: /\/projects\/[^/]+\/groups(\/[^/]+)?$/, status: 409 },
  { urlPattern: /\/projects\/[^/]+\/products\/[^/]+\/alternative$/, status: 409 },
  { urlPattern: /\/products\/[^/]+\/calculation-status$/, status: 404 },
  { urlPattern: /\/products\/[^/]+\/outputs\/select$/, status: 409 },
];

const isSuppressedError = (req: HttpRequest<unknown>, err: HttpErrorResponse): boolean =>
  suppressedErrors.some((rule) => rule.status === err.status && rule.urlPattern.test(req.url));

const getErrorDetails = (err: HttpErrorResponse) => {
  // Add context specific error messages until a generic error modal is agreed on.
  const errors = err.error?.errors;
  if (errors) {
    const firstKey = Object.keys(errors)[0];
    if (firstKey && errors[firstKey]?.[0]) {
      return errors[firstKey][0];
    }
  }
  if (err.status === 403) {
    return 'You do not have permission to perform this action.';
  }
  return err.error?.message ?? err.message;
};
